import { SIZES } from './constants'
import objectAssign from 'object-assign'

let detections
let watching = false

/**
 * @typedef {[number, number]} Range The width and height
 */

/**
 * Initialize screen size detection, so handlers could be added to the different
 * screen width ranges
 *
 * @param {Object<string,Range>} sizes
 * @returns {Object<string, MediaQueryList>}
 */
export default function detection (sizes = SIZES) {
  const targetWindow = window
  if (typeof targetWindow.matchMedia !== 'function') {
    return console.error('Browser does not support matchMedia')
  }
  if (!detections) {
    detections = Object.keys(sizes).reduce((detections, deviceType) => {
      const query = sizes[deviceType]
        .map(toQuery)
        .filter(Boolean)
        .join(' and ')
      const mediaQueryList = targetWindow.matchMedia(query)
      return objectAssign({}, detections, { [deviceType]: mediaQueryList })
    }, {})
  }
  return detections
}

/**
 * Start watching the different screen width ranges and
 * add the class for the matching screen size to the Window's <html> element
 *
 * @param {Object<string,Range>=} sizes
 */
export function viewportDetection (sizes) {
  if (!watching) {
    watching = true
    const targetWindow = window
    const detections = detection(sizes)
    Object.keys(detections).forEach(deviceType => {
      const updater = updateMatches(targetWindow, deviceType)
      const mediaQueryList = detections[deviceType]
      mediaQueryList.addListener(updater)
      updater.call(mediaQueryList)
    })
  }
}

/**
 * Creates an event listener function to update the class of the <html> element
 * of the passed window by adding or removing the device type related name.
 *
 * @param {Window} targetWindow
 * @param {string} deviceType
 *
 * @returns {EventListener}
 */
function updateMatches (targetWindow, deviceType) {
  /**
   * @this {MediaQueryList}
   * @param {MediaQueryListEvent} e
   */
  function updateClass (e) {
    const target = e || this
    const className = 'zb-is-viewport-' + deviceType
    const documentElement = targetWindow.document.documentElement
    documentElement.className = getUpdatedClasses(
      (documentElement.className || '').split(/\s+/),
      className,
      target.matches
    )
      .filter(String)
      .join(' ')
  }
  return updateClass
}

/**
 * Generate a list of class names.
 *
 * @param {[string]} classList
 * @param {string} className
 * @param {boolean} matches
 * @returns {[string]}
 */
function getUpdatedClasses (classList, className, matches) {
  return matches
    ? classList.concat(className)
    : classList.filter(c => c !== className)
}

/**
 * Generate a media type query fragment.
 *
 * @param {number} size
 * @param {number} index
 * @returns {string|0} The media query fragment
 */
function toQuery (size, index) {
  return size ? `(${index ? 'max' : 'min'}-width: ${size}px)` : size
}
